import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { WpAjaxService } from './wp-ajax/wp-ajax.service';
import { PostRequestsService } from './post-requests.service';
import { CookieHelperService } from './cookie-helper.service';
import { VehicleItem } from '../../api/models/vehicle-item';
import { Subject } from 'rxjs/Subject';
import { takeUntil } from 'rxjs/operators';
import { LoanOffer } from '../../api/models/loan-offer';
import { SessionHelperService } from './session-helper.service';
import { GetUserDealDetailsResponse, UserDetails } from '../../api/models/user-details';
import { BuyProgram } from '../../api/models/buy-programs';
import { AutoLoanService } from './auto-loans/auto-loan.service';

@Injectable()
export class CommonHelpersService {

    private _cancelUpdateOffer = new Subject<void>();
    constructor(
        private _ajax: WpAjaxService,
        private _post: PostRequestsService,
        private _autoLoanService: AutoLoanService,
        private _cookieService: CookieHelperService,
        private _sessionService: SessionHelperService
    ) { }

    /**
     * This function sets multiple attributes in @param el
     * @param el
     * @param attrs
     */
    setAttributes(el, attrs) {
        Object.keys(attrs).forEach(key => el.setAttribute(key, attrs[key]));
    }

    removeAttributes(el, attrs) {
        attrs.forEach(attr => el.removeAttribute(attr));
    }

    formatValue(value: string, pattern: RegExp, match: string): string {
        let formatted = '';

        if (value === null || value === undefined || value === '') {
            return formatted;
        }

        formatted = value.replace(pattern, match);

        return formatted;
    }

    generatePdfVoucher(pdfData: any): void {

        this._ajax.post_request({
            request_type: 'generatePDF',
            placeholders: JSON.stringify(pdfData)
        })
        .subscribe(
            response => {
                this._post.post_request(
                    '/pdf-generator/',
                    {
                        pdf: response.data,
                        filename: pdfData.name ? 'Loan-Approval-' + pdfData.name.replace(' ', '-') + '.pdf' : 'Loan-Approval.pdf'
                    }
                );
            },
            error => {
                console.log(error);
            }
        );

    }

    getLeadDetails (
        appId: string,
        sessionGUID: string = null,
        updateFromService: boolean = false
    ): Observable<GetUserDealDetailsResponse> {
        // const currentUserDetails = <UserProfile> this._cookieService._getCookie('WDUserDetails');
        return new Observable((observable) => {
            this._sessionService.getSession([
                'BuyPrograms',
                'WDUserDetails',
                'VehicleDetails',
                'SelectedBuyProgram'
            ], sessionGUID).subscribe(results => {
                const buyPrograms: BuyProgram[] = results['BuyPrograms'];
                const userDetails: UserDetails = results['WDUserDetails'];
                const vehicleDetails: VehicleItem = results['VehicleDetails'];
                const selectedBuyProgram: BuyProgram = results['SelectedBuyProgram'];

                const payload = {
                    request_type: 'getUserDetails',
                    DealId: appId
                };

                if (userDetails !== null && userDetails !== undefined) {
                    if (!updateFromService) {
                        observable.next({
                            BuyPrograms: buyPrograms,
                            LeadDetails: userDetails,
                            VehicleDetails: vehicleDetails,
                            SelectedBuyProgram: selectedBuyProgram,
                        });
                        observable.complete();
                        return;
                    }
                }

                this._autoLoanService
                    .getUserDetails(payload)
                    .subscribe(_r => {
                        if (!_r.success) {
                            return observable.error(null);
                        }
                        const {
                            BuyPrograms,
                            LeadDetails,
                            SelectedBuyProgram
                        } = <GetUserDealDetailsResponse> _r.data;

                        if (LeadDetails) {
                            LeadDetails.DealID = appId;
                            this._sessionService.setSessions([{
                                key: 'WDUserDetails',
                                value: LeadDetails
                            }, {
                                key: 'BuyPrograms',
                                value: BuyPrograms
                            }, {
                                key: 'SelectedBuyProgram',
                                value: SelectedBuyProgram
                            }], sessionGUID).subscribe(res => {});

                            observable.next({
                                BuyPrograms,
                                LeadDetails,
                                SelectedBuyProgram
                            });
                        }
                        observable.complete();
                    }, err => observable.error(err), () => observable.complete());
            });
        });
    }

    getUserDetails (
        appId: string,
        sessionGUID: string = null,
        updateFromService: boolean = false,
        storageKey: string = 'WDUserDetails'
    ): Observable<UserDetails> {
        return new Observable((observable) => {
            this._sessionService.getSession(storageKey, sessionGUID).subscribe(
                result => {
                    const payload = {
                        request_type: 'getUserDetails',
                    };
                    const currentUserDetails = <UserDetails> result;
                    if (currentUserDetails !== null && currentUserDetails !== undefined) {
                        if (currentUserDetails.DealID === appId) {
                            if (!updateFromService) {
                                return observable.next(currentUserDetails);
                            }
                        }
                    }

                    if (appId !== null && appId !== undefined) {
                        if (appId !== '') {
                            payload['DealId'] = appId;
                        }
                    }

                    this._ajax
                        .post_request(payload)
                        .subscribe(_r => {
                            if (!_r.success) {
                                return observable.error(null);
                            }
                            const {
                                LeadDetails
                            } = <GetUserDealDetailsResponse>  _r.data;

                            LeadDetails.DealID = appId;
                            LeadDetails.ExpiryDate = LeadDetails.ExpiryDate.split(' ')[0];
                            this._sessionService.setSession(storageKey, LeadDetails, sessionGUID)
                                .subscribe(res => {});
                            observable.next(LeadDetails);
                        }, err => observable.error(err));
                }
            );
        });
    }

    getInitialOffer (
        appId: string,
        sessionGUID: string = null,
        storageKey: string = 'InitialBuyPrograms'
    ): Observable<BuyProgram[]> {
        return new Observable((observable) => {
            this._sessionService.getSession(storageKey, sessionGUID).subscribe(
                result => {
                    const payload = {
                        request_type: 'getUserDetails',
                    };
                    const prequalResponse = <any> result;
                    if (prequalResponse !== null && prequalResponse !== undefined) {
                        if (prequalResponse.DealId === appId) {
                            return observable.next(prequalResponse.BuyPrograms);
                        }
                    }

                    this._ajax
                        .post_request(payload)
                        .subscribe(_r => {
                            if (!_r.success) {
                                return observable.error(null);
                            }
                            const {
                                BuyPrograms
                            } = <GetUserDealDetailsResponse>  _r.data;

                            this._sessionService.setSession(storageKey, BuyPrograms, sessionGUID)
                                .subscribe(res => {});
                            observable.next(BuyPrograms);
                        }, err => observable.error(err));
                }
            );
        });
    }

    updateStoredLeadDetails (
        userProfile: UserDetails,
        successCallBack = (res) => {},
        errorCallBack = (err) => {},
        alwaysCallBack = () => {}
    ): void {
        this._sessionService.setSession('WDUserDetails', userProfile).subscribe(successCallBack, errorCallBack, alwaysCallBack);
        // this._sessionService.setSession('WDUserDetails', userProfile).subscribe(successCallBack, errorCallBack, alwaysCallBack);
        // this._cookieService._setCookie('WDUserDetails', JSON.stringify(userProfile));
    }

    getInitialBuyPrograms (updateFromService: boolean = false): Observable<LoanOffer> {

        return new Observable((observable) => {
            const prequalResponse = this._cookieService._getCookie('prequalResponse')
                                        ? this._cookieService._getCookie('prequalResponse')['data'] : null;
            if (prequalResponse !== null) {
                const {
                    initialOffers,
                } = prequalResponse;

                if (initialOffers !== null && initialOffers !== undefined && !updateFromService) {
                    try {
                        const initialOfferResponse =
                            <LoanOffer> (typeof initialOffers === 'object' ? initialOffers : JSON.parse(initialOffers));
                        return observable.next(initialOfferResponse);
                    } catch (e) {

                    }
                }
                const payload: any = {
                    request_type: 'loanOffers',
                    velocifyId: (prequalResponse['leadId']) ? prequalResponse['leadId'] : '',
                    sessionId: (prequalResponse['sessionId']) ?  prequalResponse['sessionId'] : '',
                };

                // Fall back to call the service incase there is something wrong with the cookie data.
                return this._ajax
                    .post_request(payload)
                    .subscribe(_r => {
                        if (!_r.success) {
                            return observable.error(null);
                        }
                        this.updateBuyPrograms(_r.data);
                        observable.next(<LoanOffer> _r.data);
                    }, err => observable.error(err));
            }
            return observable.error(null);
        });
    }

    getBuyPrograms (velocifyId: string, sessionGUID: string): Observable<LoanOffer> {
        return new Observable((observable) => {
            const payload: any = {
                request_type: 'loanOffers',
                velocifyId: velocifyId,
                sessionId: sessionGUID,
            };

            // Fall back to call the service incase there is something wrong with the cookie data.
            return this._ajax
                .post_request(payload)
                .subscribe(_r => {
                    if (!_r.success) {
                        return observable.error(null);
                    }
                    // this.updateBuyPrograms(_r.data);
                    observable.next(<LoanOffer> _r.data);
                }, err => observable.error(err));
        });
    }

    updateBuyPrograms (loanOffer: LoanOffer) {
        const prequalResponse = this._cookieService._getCookie('prequalResponse')
        ? this._cookieService._getCookie('prequalResponse')['data'] : null;
        if (prequalResponse !== null) {
            const {
                initialOffers,
            } = prequalResponse;
            let initialOfferResponse = null;

            if (initialOffers !== null && initialOffers !== undefined) {
                try {
                    initialOfferResponse =
                        <LoanOffer> (typeof initialOffers === 'object' ? initialOffers : JSON.parse(initialOffers));
                } catch (e) {

                }
            }
            prequalResponse.initialOffers = Object.assign({
                ...(initialOffers !== null ? initialOffers : {})
            }, loanOffer);
            this._cookieService._setCookie('prequalResponse', JSON.stringify({
                data: prequalResponse
            }));
        }
    }

    /*
    * Method for update offer
    * */
   updateOffer(
        dealId?: number,
        velocifyId?: string,
        sessionId?: string,
        id?: number,
        buyProgram?: any,
        monthlyPayment?: number,
        inquiryId?: string,
        approvalDate?: string,
        expirationDate?: string,
        approvalId?: number,
        ecsType?: number,
        gapWaiver?: boolean,
        isbuyprogramExecRequired?: boolean,
        inventoryId?: string,
        vehicle?: VehicleItem
    ) {

        this._cancelUpdateOffer.next();
        this._cancelUpdateOffer.complete();

        const _data = {
            request_type: 'updateOffer',
            dealId: dealId,
            velocifyId : velocifyId,
            sessionId : sessionId,
            id: id,
            buyProgram: buyProgram,
            monthlyPayment : monthlyPayment,
            inquiryId: inquiryId,
            approvalDate : approvalDate,
            expirationDate : expirationDate,
            approvalId : approvalId,
            ecsType: ecsType,
            gapWaiver: gapWaiver,
            isbuyprogramExecRequired: isbuyprogramExecRequired,
            inventoryId: inventoryId,
            VehicleDetails: vehicle ? vehicle : null
        };

        return this._autoLoanService.updateOffer(_data)
            .pipe(takeUntil(this._cancelUpdateOffer));

    }

    getDealApprovalDate(expiryDate?: string): string {
        let prequalDate: Date = null;
        if (expiryDate) {
            prequalDate = new Date(expiryDate);
            prequalDate.setDate(prequalDate.getDate() - 30);
            return `${this.padLeft(prequalDate.getMonth() + 1, 2)}/${this.padLeft(prequalDate.getDate(), 2)}/${prequalDate.getFullYear()}`;
        }

        return null;
    }

    padLeft (val, size: number = 1): string {
        const pad = size + 1;
        const length = val.toString().length;
        if (length < pad) {
            const padder = new Array(pad - length);
            return `${padder.join('0')}${val}`;
        }
        return val;
    }

}



// WEBPACK FOOTER //
// ./src/app/shared/services/common-helpers.service.ts